'use client'

import axios from 'axios'
import {chargeBackHost} from 'GLOBAL/envVariables'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

type OptionItem = {
  value: string
  label: string
}

type MultiSelectProps = {
  officeValue: string
  onChange: (values: string[]) => void
}

const CheckDayMultiSelect = ({officeValue, onChange}: MultiSelectProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<OptionItem[]>([])

  const getLabel = useCallback((item: string | OptionItem) => {
    return typeof item === 'string' ? item : item.label
  }, [])

  const getValue = useCallback((item: string | OptionItem) => {
    return typeof item === 'string' ? item : item.value
  }, [])

  const optionValues = useMemo(() => options.map(getValue), [options, getValue])
  useEffect(() => {
    onChange(selectedOptions)
  }, [selectedOptions, onChange])

  useEffect(() => {
    const fetchDays = async () => {
      if (!officeValue) {
        setOptions([])
        setSelectedOptions([])
        return
      }

      try {
        const res = await axios.get(`${chargeBackHost}/api/check_day?office=${officeValue}`)
        const days: {check_day: string}[] = res.data

        const mapped = [
          {value: 'all', label: '검침일 전체'},
          ...days.map((d) => ({
            value: d.check_day,
            label: `${d.check_day}일`,
          })),
        ]

        setOptions(mapped)

        setSelectedOptions((prev) => {
          const validPrev = prev.filter((v) => mapped.some((opt) => opt.value === v))
          if (validPrev.length > 0) return validPrev
          return ['all']
        })
      } catch (err) {
        console.error('검침일 조회 실패:', err)
        setOptions([])
        setSelectedOptions([])
      }
    }

    fetchDays()
  }, [officeValue])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOption = (option: string | OptionItem) => {
    const value = getValue(option)

    if (value === 'all') {
      setSelectedOptions(['all'])
      return
    }

    const isSelected = selectedOptions.includes(value)
    const updated = isSelected
      ? selectedOptions.filter((item) => item !== value)
      : [...selectedOptions.filter((item) => item !== 'all'), value]

    const withoutAll = updated.filter((item) => item !== 'all')
    if (withoutAll.length === 0) {
      setSelectedOptions(['all'])
      return
    }

    setSelectedOptions(updated)
  }

  if (options.length === 0) return null

  return (
    <div className='relative' ref={wrapperRef}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        className='border rounded-md px-4 py-3 bg-white text-lg text-gray-800 w-60 cursor-pointer
             truncate whitespace-nowrap overflow-hidden'
        title={selectedOptions
          .slice()
          .sort((a, b) =>
            getLabel(options.find((item) => getValue(item) === a) || a).localeCompare(
              getLabel(options.find((item) => getValue(item) === b) || b)
            )
          )
          .map((val) => getLabel(options.find((item) => getValue(item) === val) || val))
          .join(', ')}
      >
        {(() => {
          const selectedWithoutAll = selectedOptions.filter((v) => v !== 'all')
          const allValuesExceptAll = optionValues.filter((v) => v !== 'all')

          if (
            selectedWithoutAll.length === allValuesExceptAll.length &&
            selectedOptions.includes('all')
          ) {
            return '검침일 전체'
          }

          return selectedOptions
            .slice()
            .sort((a, b) =>
              getLabel(options.find((item) => getValue(item) === a) || a).localeCompare(
                getLabel(options.find((item) => getValue(item) === b) || b)
              )
            )
            .map((val) => getLabel(options.find((item) => getValue(item) === val) || val))
            .join(', ')
        })()}
      </div>

      {open && (
        <div className='absolute top-full mt-2 border rounded-md bg-white shadow-md z-10 w-60 max-h-80 overflow-auto'>
          {options.map((option) => {
            const value = getValue(option)
            return (
              <label
                key={value}
                className='flex items-center px-4 py-3 text-lg hover:bg-gray-100 cursor-pointer'
              >
                <input
                  type='checkbox'
                  className='mr-2'
                  checked={selectedOptions.includes(value)}
                  onChange={() => toggleOption(option)}
                />
                {getLabel(option)}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CheckDayMultiSelect
